'use client';

import type { LiveVoiceSession } from '@/lib/live-voice-session';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { QueuedRealtimeInternalMessage } from '@/lib/realtime-voice-agent';
import {
  buildRealtimeQuestionnaireStepPrompt,
  getActiveRealtimeVoiceQuestionnaire,
  subscribeActiveRealtimeVoiceQuestionnaire,
  type ActiveRealtimeVoiceQuestionnaire,
} from '@/lib/realtime-voice-questionnaire';

type Args = {
  open: boolean;
  orgThreadId?: string;
  sessionRef: React.MutableRefObject<LiveVoiceSession | null>;
  queueOrSendInternalMessage: (item: QueuedRealtimeInternalMessage) => void;
};

export function useRealtimeQuestionnairePrompts({
  open,
  orgThreadId,
  sessionRef,
  queueOrSendInternalMessage,
}: Args) {
  const lastReadKeyRef = useRef<string | null>(null);
  const lastToolCallIdRef = useRef<string | null>(null);
  const pendingPromptKeyRef = useRef<string | null>(null);
  const speakingPromptKeyRef = useRef<string | null>(null);
  const interruptedPromptKeyRef = useRef<string | null>(null);
  const scheduledManualPromptKeyRef = useRef<string | null>(null);
  const manualPromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const activeQuestionnaire = useSyncExternalStore(
    subscribeActiveRealtimeVoiceQuestionnaire,
    () =>
      orgThreadId ? getActiveRealtimeVoiceQuestionnaire(orgThreadId) : null,
    () => null
  );

  const clearManualPromptTimer = useCallback(() => {
    if (manualPromptTimerRef.current) {
      clearTimeout(manualPromptTimerRef.current);
      manualPromptTimerRef.current = null;
    }
    scheduledManualPromptKeyRef.current = null;
  }, []);

  const reset = useCallback(() => {
    clearManualPromptTimer();
    lastReadKeyRef.current = null;
    lastToolCallIdRef.current = null;
    pendingPromptKeyRef.current = null;
    speakingPromptKeyRef.current = null;
    interruptedPromptKeyRef.current = null;
  }, [clearManualPromptTimer]);

  const sendStepPrompt = useCallback(
    (
      questionnaire: ActiveRealtimeVoiceQuestionnaire,
      readRequestKey: string,
      options: { interruptActiveQuestionnaireSpeech?: boolean } = {}
    ) => {
      const session = sessionRef.current;
      if (!session || lastReadKeyRef.current === readRequestKey) return;

      clearManualPromptTimer();
      lastReadKeyRef.current = readRequestKey;
      if (options.interruptActiveQuestionnaireSpeech) {
        interruptedPromptKeyRef.current =
          speakingPromptKeyRef.current ?? pendingPromptKeyRef.current;
        session.interrupt();
      }
      pendingPromptKeyRef.current = questionnaire.promptKey;
      queueOrSendInternalMessage({
        key: `questionnaire:${questionnaire.promptKey}`,
        message: buildRealtimeQuestionnaireStepPrompt(questionnaire),
        ticket: {
          kind: 'questionnaire_prompt',
          promptKey: questionnaire.promptKey,
          reason: 'visible_questionnaire_prompt',
        },
      });
    },
    [clearManualPromptTimer, queueOrSendInternalMessage, sessionRef]
  );

  useEffect(() => {
    if (!open || !activeQuestionnaire || !sessionRef.current) {
      clearManualPromptTimer();
      return;
    }

    if (activeQuestionnaire.readAloudRequestKey) {
      lastToolCallIdRef.current = activeQuestionnaire.toolCallId;
      sendStepPrompt(
        activeQuestionnaire,
        `voice:${activeQuestionnaire.readAloudRequestKey}`
      );
      return;
    }

    if (lastToolCallIdRef.current !== activeQuestionnaire.toolCallId) {
      lastToolCallIdRef.current = activeQuestionnaire.toolCallId;
      sendStepPrompt(
        activeQuestionnaire,
        `questionnaire:${activeQuestionnaire.toolCallId}:${activeQuestionnaire.promptKey}`
      );
      return;
    }

    const currentPromptKey =
      speakingPromptKeyRef.current ?? pendingPromptKeyRef.current;
    if (
      !currentPromptKey ||
      currentPromptKey === activeQuestionnaire.promptKey
    ) {
      clearManualPromptTimer();
      return;
    }
    if (scheduledManualPromptKeyRef.current === activeQuestionnaire.promptKey) {
      return;
    }

    clearManualPromptTimer();
    scheduledManualPromptKeyRef.current = activeQuestionnaire.promptKey;
    manualPromptTimerRef.current = setTimeout(() => {
      manualPromptTimerRef.current = null;
      scheduledManualPromptKeyRef.current = null;
      if (!sessionRef.current) return;

      const latestPromptKey =
        speakingPromptKeyRef.current ?? pendingPromptKeyRef.current;
      if (
        !latestPromptKey ||
        latestPromptKey === activeQuestionnaire.promptKey
      ) {
        return;
      }
      sendStepPrompt(
        activeQuestionnaire,
        `manual:${activeQuestionnaire.promptKey}`,
        { interruptActiveQuestionnaireSpeech: true }
      );
    }, 150);

    return clearManualPromptTimer;
  }, [
    activeQuestionnaire,
    clearManualPromptTimer,
    open,
    sendStepPrompt,
    sessionRef,
  ]);

  return {
    activeQuestionnaire,
    lastToolCallIdRef,
    pendingPromptKeyRef,
    speakingPromptKeyRef,
    interruptedPromptKeyRef,
    reset,
    sendStepPrompt,
  };
}
